import React, { useState, useEffect } from 'react';
import { Anchor, Button } from '@keplerco/core';
import styles from '../freemium.onboarding.module.css';
import classNames from 'classnames';
import layoutStyles from '../../../../design/layout.module.css';
import { useAppActions, useAppState } from '../../../../overmind';
import { RoleTaxonomyDto } from '../../../../models/overmind/analytics';
import { PagePath } from '../../../../navigation/navigation.enums';
import { FetchType } from '../../../../enums';
import { EntitiesCardWidget } from '../../../../components/cards/entities-card.widget';
import { InsightsWidget } from '../insights/insights-widget';
import { DSMMatrix } from '../../components/dsm-matrix/dsm-matrix';

// Props for the summary page
interface SummaryPageProps {
  onBack: () => void;
  onComplete: () => void;
}

/**
 * Summary onboarding step
 * Shows a summary of all collected information and finalizes the onboarding process
 */
export function SummaryPage({ onBack, onComplete }: SummaryPageProps): JSX.Element {
  const actions = useAppActions();
  const { freemiumAccount } = useAppState();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [roles, setRoles] = useState<Array<RoleTaxonomyDto>>();

  useEffect(() => {
    async function getRoles() {
      if (!freemiumAccount) return;
      const rolesResponse = await actions.getRoleSkillTaxonomy({
        companySlug: freemiumAccount.companySlug,
        search: '',
        skillRank: 10,
        pageSize: 10,
        page: 1
      });
      if (!!rolesResponse) {
        setRoles(rolesResponse.roles);
      }
    }
    getRoles();
  }, []);

  const getTotalSkillsCount = (): number => {
    if (!roles) return 0;
    return roles.reduce((count, role) => count + role.skills.length, 0);
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    setError(null);
    // Use FetchType.PageFetching instead of a non-existent value
    actions.startLoader({ path: PagePath.onboarding, type: FetchType.PageFetching });

    try {
      // Set the onboarding as completed
      await actions.tryCompleteInitialOnboarding();
      await actions.getFreemiumAccount();
      await actions.getUser();

      actions.stopLoader(PagePath.onboarding);
      // Navigate to the dashboard after successful completion
      setIsSubmitting(false);
      actions.stopLoader(PagePath.onboarding);

      // Use the onComplete callback from props, which will handle navigation
      onComplete();
    } catch (error) {
      setError('An error occurred while completing the onboarding process. Please try again.');
      setIsSubmitting(false);
      actions.stopLoader(PagePath.onboarding);
    }
  };

  const handleBack = () => {
    onBack();
  };

  return !!freemiumAccount ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      <EntitiesCardWidget title="Review Your Taxonomy Preview" description="Based on your inputs, our AI has generated a preview of potential roles and skills for your organization.">
        {/* AI-Generated Taxonomy Section */}
        <div className={styles.statisticsContainer}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{roles?.length || 0}</div>
            <div className={styles.statLabel}>Roles</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{getTotalSkillsCount()}</div>
            <div className={styles.statLabel}>Skills</div>
          </div>
        </div>
        <div className={styles.summaryContent}>
          {/* Roles and Skills Summary */}
          <DSMMatrix />
          <p className={styles.summaryNote} style={{ marginTop: '16px', fontSize: '0.9rem' }}>
            This is just a preview. After completing the setup, our system will generate a complete, tailored roles and skills taxonomy for your organization.
          </p>
        </div>
      </EntitiesCardWidget>

      <EntitiesCardWidget title="Insights" description="AI-generated insights based on your company data">
        <InsightsWidget
          insights={freemiumAccount.insights || []}
          maxInsights={5}
          showUpgradePrompt={true}
        />
      </EntitiesCardWidget>

      <footer className={classNames('card', layoutStyles.wizardFooter)}>
        <Anchor className={styles.anchor} onClick={handleBack} disabled={isSubmitting}>
          Back
        </Anchor>
        <Button
          type="button"
          filled
          onClick={handleComplete}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Processing...' : 'Complete'}
        </Button>
      </footer>
    </div>
  ) : <></>;
}
